package fyi.moko.hotpl.ui.home

import androidx.compose.runtime.getValue
import fyi.moko.hotpl.data.service.SyncCoordinator
import fyi.moko.hotpl.domain.service.EmotionLabelPreference
import fyi.moko.hotpl.domain.service.HomeViewPreference
import fyi.moko.hotpl.domain.service.friendsWhoAlsoWantToGo
import fyi.moko.hotpl.domain.service.UsageEvents
import fyi.moko.hotpl.domain.service.UsageTracker
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import fyi.moko.hotpl.data.backup.BackupImportPreview
import fyi.moko.hotpl.data.backup.PreparedHotplBackup
import fyi.moko.hotpl.domain.model.Emotion
import fyi.moko.hotpl.domain.model.GeoPoint
import fyi.moko.hotpl.domain.model.haversineMeters
import fyi.moko.hotpl.domain.model.Place
import fyi.moko.hotpl.domain.model.alphabetBucket
import fyi.moko.hotpl.domain.model.isWantToGo
import fyi.moko.hotpl.domain.model.placeNameSortKey
import fyi.moko.hotpl.domain.model.PlaceCategory
import fyi.moko.hotpl.domain.model.PlaceVisitSummary
import fyi.moko.hotpl.domain.model.RechargeCandidate
import fyi.moko.hotpl.domain.model.Visit
import fyi.moko.hotpl.domain.model.normalizeForComparison
import fyi.moko.hotpl.domain.usecase.DeletePlaceUseCase
import fyi.moko.hotpl.domain.usecase.ExportBackupUseCase
import fyi.moko.hotpl.domain.usecase.LoadSampleDataUseCase
import fyi.moko.hotpl.domain.usecase.MergeDuplicatePlacesUseCase
import fyi.moko.hotpl.domain.usecase.ObserveAllVisitsUseCase
import fyi.moko.hotpl.domain.usecase.ObservePlacesUseCase
import fyi.moko.hotpl.domain.usecase.ObserveFriendWishes
import fyi.moko.hotpl.domain.usecase.ObserveVisitSummariesUseCase
import fyi.moko.hotpl.domain.usecase.PrepareImportBackupUseCase
import fyi.moko.hotpl.domain.usecase.RechargePlaceSelector
import fyi.moko.hotpl.domain.usecase.RestoreBackupUseCase
import fyi.moko.hotpl.domain.usecase.RevisitWindowCalculator
import fyi.moko.hotpl.ui.util.RecallCandidate
import fyi.moko.hotpl.ui.util.todaysRecalls
import kotlinx.datetime.daysUntil
import kotlinx.datetime.toLocalDateTime
import kotlin.time.Clock
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.flowOn
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.flow.receiveAsFlow

/**
 * 가본 곳 / 안 가본 곳 (리뷰어 R-11). 상호배타 — "어떤 날은 안 가본 곳에 가고 싶고,
 * 반대인 경우도 있다"는 말은 필터가 아니라 **모드**다. 한 번에 한쪽만 본다.
 */
enum class HomeVisitFilter { ALL, BEEN, WANT_TO_GO }

enum class HomeSortMode {
    DEFAULT,
    MOST_RECENT,
    NEAREST,

    /**
     * 이름 A→Z. A-Z 색인 레일은 **이 모드에서만** 뜬다 — 거리순으로 늘어선 목록에서 "M"을
     * 눌러 봐야 갈 곳이 없다(리뷰어 R-10).
     */
    NAME,
}

enum class BackupActionType {
    PREPARE_RESTORE,
    CONFIRM_RESTORE,
    LOAD_SAMPLE
}

data class BackupError(
    val message: String,
    val actionType: BackupActionType
)

sealed interface HomeUiEvent {
    data class ShowMessage(val message: String) : HomeUiEvent
}

data class HomeUiState(
    val places: List<Place> = emptyList(),
    val visitSummaries: Map<String, PlaceVisitSummary> = emptyMap(),
    val allTags: List<String> = emptyList(),
    /**
     * Categories actually represented in the user's places, in [PlaceCategory] declaration
     * order. Only these get a chip — offering "Beach" to someone whose journal is entirely
     * cafes is a filter that can only ever return nothing.
     */
    val availableCategories: List<PlaceCategory> = emptyList(),
    val query: String = "",
    val selectedTags: Set<String> = emptySet(),
    /** OR-ed, unlike [selectedTags]: a place has one category, so AND-ing them matches nothing. */
    val selectedCategories: Set<PlaceCategory> = emptySet(),
    val likedFilter: Boolean? = null,
    val visitFilter: HomeVisitFilter = HomeVisitFilter.ALL,
    val selectedPlaceId: String? = null,
    val sortMode: HomeSortMode = HomeSortMode.DEFAULT,
    /**
     * 글자 → 그 글자의 첫 항목 인덱스. [HomeSortMode.NAME]일 때만 채워진다
     * ([alphabetIndex]). 목록이 [MIN_PLACES_FOR_ALPHABET_RAIL]보다 짧으면 화면이 레일을
     * 그리지 않는다 — 값 자체는 여기 그대로 있다.
     */
    val alphabetIndex: Map<Char, Int> = emptyMap(),
    val totalCount: Int = 0,
    val recalls: List<RecallCandidate> = emptyList(),
    val revisitCandidates: List<RevisitCandidate> = emptyList(),
    /**
     * The 회복 루프 (§1.4): emotions the user has actually recorded, the one currently
     * asked for, and the places that answer it. Empty [rechargeEmotions] means the picker
     * renders nothing at all — a brand-new account has no feelings to ask about yet.
     */
    val rechargeEmotions: List<Emotion> = emptyList(),
    val selectedRechargeEmotion: Emotion? = null,
    val rechargeCandidates: List<RechargeCandidate> = emptyList(),
    val loading: Boolean = true,
    val isBackupLoading: Boolean = false,
    /**
     * True when the first remote pull completed and the account has zero places — a
     * hint to show "this account has no saved places" so the user can distinguish an
     * empty remote dataset from a sync that hasn't happened yet (backlog 0-9).
     */
    val showEmptyAccountHint: Boolean = false,
) {
    val isFiltering: Boolean
        get() = query.isNotBlank() || selectedTags.isNotEmpty() ||
            selectedCategories.isNotEmpty() || likedFilter != null ||
            visitFilter != HomeVisitFilter.ALL
    val hasActiveFiltersOrSort: Boolean get() = isFiltering || sortMode != HomeSortMode.DEFAULT
    val visibleCount: Int get() = places.size
}
class HomeViewModel(
    observePlaces: ObservePlacesUseCase,
    observeVisitSummaries: ObserveVisitSummariesUseCase,
    observeAllVisits: ObserveAllVisitsUseCase,
    observeFriendWishes: ObserveFriendWishes,
    private val exportBackup: ExportBackupUseCase,
    private val prepareImportBackup: PrepareImportBackupUseCase,
    private val restoreBackup: RestoreBackupUseCase,
    private val deletePlaceUseCase: DeletePlaceUseCase,
    private val loadSampleDataUseCase: LoadSampleDataUseCase,
    private val mergeDuplicatePlacesUseCase: MergeDuplicatePlacesUseCase,
    private val syncCoordinator: SyncCoordinator,
    private val homeViewPreference: HomeViewPreference,
    emotionLabelPreference: EmotionLabelPreference,
    private val usageTracker: UsageTracker,
    private val revisitWindowCalculator: RevisitWindowCalculator = RevisitWindowCalculator(),
    private val rechargePlaceSelector: RechargePlaceSelector = RechargePlaceSelector(),
) : ViewModel() {

    init {
        syncCoordinator.startSync(viewModelScope)
    }

    private val query = MutableStateFlow("")

    /**
     * ── 알려진 한계 ──────────────────────────────
     *  · TextField는 이 raw StateFlow에 직접 바인딩해야 한다 (uiState.query 금지).
     *    uiState는 combine()을 여러 겹 거쳐 flowOn(Dispatchers.Default)로 재조합되므로
     *    입력 → 반영까지 디스패처를 넘나드는 논블로킹 왕복이 생긴다. 라틴 문자는 매
     *    keystroke가 완결된 커밋이라 티가 안 나지만, 한글은 IME가 조합 중인 자모를
     *    TextField의 로컬 편집 버퍼에 들고 있는 상태라 외부에서 되돌아온 값이 그 조합을
     *    깨서 자모가 뒤섞여 찍힌다. 필터링에는 계속 uiState.query(같은 소스)를 쓰되,
     *    TextField의 value는 반드시 이 flow로 직접 collect한다.
     */
    val queryText: StateFlow<String> = query.asStateFlow()
    private val selectedTags = MutableStateFlow<Set<String>>(emptySet())
    private val selectedCategories = MutableStateFlow<Set<PlaceCategory>>(emptySet())
    private val likedFilter = MutableStateFlow<Boolean?>(null)
    private val visitFilter = MutableStateFlow(HomeVisitFilter.ALL)
    private val selectedPlaceId = MutableStateFlow<String?>(null)
    private val sortMode = MutableStateFlow(HomeSortMode.DEFAULT)
    private val currentLocation = MutableStateFlow<GeoPoint?>(null)
    private val selectedRechargeEmotion = MutableStateFlow<Emotion?>(null)
    private var preparedRestoreBackup: PreparedHotplBackup? = null
    private var lastAttemptedJson: String? = null

    private val isBackupLoading = MutableStateFlow(false)
    private val dismissedEmptyAccountHint = MutableStateFlow(false)

    init {
        // A new pull epoch (firstPullCompleted dropping back to false — account switch,
        // sign-out) re-arms the empty-account hint: dismissing it for account A must not
        // suppress it for a subsequently signed-in account B. Declared after the flag so
        // property initialization order is safe.
        viewModelScope.launch {
            syncCoordinator.firstPullCompleted.collect { completed ->
                if (!completed) dismissedEmptyAccountHint.value = false
            }
        }
    }

    var activeBackupError by mutableStateOf<BackupError?>(null)
        private set

    private val _uiEvents = Channel<HomeUiEvent>(Channel.BUFFERED)
    val uiEvents = _uiEvents.receiveAsFlow()

    var restorePreview by mutableStateOf<BackupImportPreview?>(null)
        private set

    /** Everything derived from the DB alone — computed once per data change, not per keystroke. */
    private data class HomeSourceData(
        val places: List<Place>,
        val visitSummaries: Map<String, PlaceVisitSummary>,
        val allTags: List<String>,
        val availableCategories: List<PlaceCategory>,
        val recalls: List<RecallCandidate>,
        val revisitCandidates: List<RevisitCandidate>,
        /** Kept raw so the recharge selector can re-rank on emotion change without re-reading the DB. */
        val allVisits: List<Visit>,
        val rechargeEmotions: List<Emotion>,
    )

    private val sourceData: Flow<HomeSourceData> = combine(
        observePlaces(),
        observeVisitSummaries(),
        observeAllVisits(),
    ) { places, summaries, allVisits ->
        // Recall is derived from the full (unfiltered) set so search/filters never hide it.
        val nowMs = Clock.System.now().toEpochMilliseconds()
        val visitsByPlace = allVisits.groupBy { it.placeId }.mapValues { entry -> entry.value.map { it.visitedAt } }
        val visitCountByPlace = allVisits.groupBy { it.placeId }.mapValues { it.value.size }
        HomeSourceData(
            places = places,
            visitSummaries = summaries.associateBy { it.placeId },
            allTags = places.flatMap { it.tags }.distinct().sorted(),
            availableCategories = availableCategories(places),
            recalls = todaysRecalls(places, visitsByPlace, nowMs),
            revisitCandidates = revisitCandidates(places, visitCountByPlace, summaries.associateBy { it.placeId }, nowMs),
            allVisits = allVisits,
            rechargeEmotions = rechargePlaceSelector.availableEmotions(places, allVisits),
        )
    }.distinctUntilChanged()

    /** The 회복 루프 slice, re-ranked only when the data or the asked-for feeling changes. */
    private data class RechargeState(
        val emotions: List<Emotion> = emptyList(),
        val selected: Emotion? = null,
        val candidates: List<RechargeCandidate> = emptyList(),
    )

    private val rechargeState: Flow<RechargeState> = combine(
        sourceData,
        selectedRechargeEmotion,
    ) { data, emotion ->
        // A feeling that drops out of the data (last matching visit deleted) must not stay
        // selected — the picker would show a chip the list can no longer answer.
        val selected = emotion?.takeIf { it in data.rechargeEmotions }
        RechargeState(
            emotions = data.rechargeEmotions,
            selected = selected,
            candidates = selected?.let { rechargePlaceSelector.select(data.places, data.allVisits, it) }.orEmpty(),
        )
    }.distinctUntilChanged()

    private val baseUiState: Flow<HomeUiState> = combine(
        sourceData,
        query,
        combine(selectedTags, selectedPlaceId, likedFilter, selectedCategories, visitFilter, ::FilterSelections),
        combine(isBackupLoading, syncCoordinator.firstPullCompleted, dismissedEmptyAccountHint) { loadingBackup, firstPull, dismissed ->
            Triple(loadingBackup, firstPull, dismissed)
        },
        rechargeState,
    ) { data, q, filters, (loadingBackup, firstPull, dismissedHint), recharge ->
        val (tags, selectedId, liked, categories, visited) = filters
        val showHint = firstPull && data.places.isEmpty() && !dismissedHint
        HomeUiState(
            places = filterHomePlaces(data.places, q, tags, liked, categories, visited, data.visitSummaries),
            visitSummaries = data.visitSummaries,
            allTags = data.allTags,
            availableCategories = data.availableCategories,
            query = q,
            selectedTags = tags,
            selectedCategories = categories,
            likedFilter = liked,
            visitFilter = visited,
            selectedPlaceId = selectedId?.takeIf { id -> data.places.any { it.id == id } },
            totalCount = data.places.size,
            recalls = data.recalls,
            revisitCandidates = data.revisitCandidates,
            rechargeEmotions = recharge.emotions,
            selectedRechargeEmotion = recharge.selected,
            rechargeCandidates = recharge.candidates,
            loading = false,
            isBackupLoading = loadingBackup,
            showEmptyAccountHint = showHint,
        )
    }

    val uiState: StateFlow<HomeUiState> = combine(
        baseUiState,
        sortMode,
        currentLocation,
    ) { state, activeSortMode, location ->
        val orderedPlaces = orderHomePlaces(
            places = state.places,
            sortMode = activeSortMode,
            visitSummaries = state.visitSummaries,
            location = location,
        )
        state.copy(
            places = orderedPlaces,
            sortMode = activeSortMode,
            alphabetIndex = alphabetIndex(orderedPlaces, activeSortMode),
        )
    }.flowOn(Dispatchers.Default).stateIn(
        scope = viewModelScope,
        started = SharingStarted.WhileSubscribed(5_000),
        initialValue = HomeUiState(loading = true),
    )

    /**
     * Journal 탭이 지도로 열릴지. 기기에 기억되므로 콜드 스타트에도 마지막 선택이 남는다
     * ([HomeViewPreference], 리뷰어 R-7).
     */
    /** User-renamed emotion slots for the recharge picker chips ([EmotionLabelPreference]). */
    val emotionLabels: StateFlow<Map<String, String>> = emotionLabelPreference.labels

    val mapFirst: StateFlow<Boolean> = homeViewPreference.mapFirst

    /**
     * placeId → display names of friends who also want to go there (0-37 stage 2). Kept
     * outside [uiState] on purpose: it comes from the network, arrives late, and is empty
     * offline — the list must never wait on it. Only Want-to-go places of mine get an entry.
     */
    val friendWishNames: StateFlow<Map<String, List<String>>> = combine(
        sourceData,
        observeFriendWishes(),
    ) { data, wishes ->
        if (wishes.isEmpty()) {
            emptyMap()
        } else {
            data.places.mapNotNull { place ->
                val visitCount = data.visitSummaries[place.id]?.visitCount ?: 0
                friendsWhoAlsoWantToGo(place, visitCount, wishes)
                    .takeIf { it.isNotEmpty() }
                    ?.let { friends -> place.id to friends.map { it.displayName } }
            }.toMap()
        }
    }.flowOn(Dispatchers.Default).stateIn(viewModelScope, SharingStarted.WhileSubscribed(5_000), emptyMap())

    fun setMapFirst(value: Boolean) {
        usageTracker.track(UsageEvents.MAP_TOGGLE, mapOf("map" to value.toString()))
        viewModelScope.launch { homeViewPreference.setMapFirst(value) }
    }

    /** Chip taps are logged by chip id + resulting state; a tag's own text stays out (0-39). */
    private fun trackFilterTap(chip: String, on: Boolean) {
        usageTracker.track(UsageEvents.FILTER_TAP, mapOf("chip" to chip, "on" to on.toString()))
    }

    fun onQueryChange(value: String) { query.value = value }

    /** Dismisses the "this account has no saved places" hint (backlog 0-9). */
    fun dismissEmptyAccountHint() {
        dismissedEmptyAccountHint.value = true
    }

    fun clearFilters() {
        usageTracker.track(UsageEvents.FILTERS_CLEAR)
        query.value = ""
        selectedTags.value = emptySet()
        selectedCategories.value = emptySet()
        likedFilter.value = null
        visitFilter.value = HomeVisitFilter.ALL
        sortMode.value = HomeSortMode.DEFAULT
    }

    fun selectPlace(id: String) {
        selectedPlaceId.value = id
    }

    /**
     * Asks "지금 이 느낌이 필요해" — or takes the question back when the same feeling is tapped
     * again. Not part of [clearFilters]: this is a question about the user's state, not a filter
     * on the list below it.
     */
    fun toggleRechargeEmotion(emotion: Emotion) {
        val on = selectedRechargeEmotion.value != emotion
        usageTracker.track(UsageEvents.RECHARGE_PICK, mapOf("emotion" to emotion.id, "on" to on.toString()))
        selectedRechargeEmotion.update { if (it == emotion) null else emotion }
    }

    fun selectMostRecentlyVisitedSort() {
        trackFilterTap("recent", on = sortMode.value != HomeSortMode.MOST_RECENT)
        sortMode.value = if (sortMode.value == HomeSortMode.MOST_RECENT) {
            HomeSortMode.DEFAULT
        } else {
            HomeSortMode.MOST_RECENT
        }
    }

    /** 이름순을 켜고 끈다 — 다시 누르면 저장소가 준 순서로 돌아온다. */
    fun selectNameSort() {
        trackFilterTap("az", on = sortMode.value != HomeSortMode.NAME)
        sortMode.value = if (sortMode.value == HomeSortMode.NAME) {
            HomeSortMode.DEFAULT
        } else {
            HomeSortMode.NAME
        }
    }

    fun selectNearestSort(location: GeoPoint) {
        trackFilterTap("nearest", on = true)
        currentLocation.value = location
        sortMode.value = HomeSortMode.NEAREST
    }

    fun clearNearestSort() {
        if (sortMode.value == HomeSortMode.NEAREST) {
            trackFilterTap("nearest", on = false)
            sortMode.value = HomeSortMode.DEFAULT
        }
    }

    /** Deletes a place and its visit history (used by the list swipe-to-delete action). */
    fun deletePlace(id: String) {
        viewModelScope.launch {
            deletePlaceUseCase(id)
        }
    }

    /** Seeds the bundled sample places (merge/append). Offered from the empty home state. */
    fun loadSampleData() {
        viewModelScope.launch {
            isBackupLoading.value = true
            activeBackupError = null
            runCatching {
                loadSampleDataUseCase()
            }.onSuccess {
                _uiEvents.send(HomeUiEvent.ShowMessage("Sample data loaded"))
            }.onFailure { e ->
                activeBackupError = BackupError(
                    message = e.message ?: "Failed to load sample data",
                    actionType = BackupActionType.LOAD_SAMPLE
                )
            }
            isBackupLoading.value = false
        }
    }

    /** Deletes every extra copy of a place saved more than once under the same name and
     * coordinates, keeping the copy with the most visits. Offered from the overflow menu as a
     * cleanup tool for duplicates an account switch left behind. */
    fun mergeDuplicatePlaces() {
        viewModelScope.launch {
            val result = mergeDuplicatePlacesUseCase()
            val message = if (result.placesRemoved > 0) {
                "Merged ${result.placesRemoved} duplicate place(s)"
            } else {
                "No duplicate places found"
            }
            _uiEvents.send(HomeUiEvent.ShowMessage(message))
        }
    }

    /** Adds [category] to the OR-ed category filter, or takes it back out. */
    fun toggleCategory(category: PlaceCategory) {
        trackFilterTap("category:${category.name}", on = category !in selectedCategories.value)
        selectedCategories.update { if (category in it) it - category else it + category }
    }

    fun toggleTag(tag: String) {
        val normalizedTag = normalizeForComparison(tag)
        if (normalizedTag.isEmpty()) return
        trackFilterTap("tag", on = normalizedTag !in selectedTags.value)
        selectedTags.update { if (normalizedTag in it) it - normalizedTag else it + normalizedTag }
    }

    /** Cycles the liked filter: null (all) → true (liked only) → null. */
    fun toggleLikedFilter() {
        trackFilterTap("liked", on = likedFilter.value == null)
        likedFilter.update { current ->
            when (current) {
                null -> true
                true -> false
                false -> null
            }
        }
    }

    /** Tapping the active mode again goes back to [HomeVisitFilter.ALL]; the two are exclusive. */
    fun toggleVisitFilter(filter: HomeVisitFilter) {
        trackFilterTap(filter.name.lowercase(), on = visitFilter.value != filter)
        visitFilter.update { current -> if (current == filter) HomeVisitFilter.ALL else filter }
    }

    suspend fun exportBackupJson(): String = exportBackup()

    suspend fun prepareRestore(json: String) {
        lastAttemptedJson = json
        isBackupLoading.value = true
        activeBackupError = null
        runCatching {
            prepareImportBackup(json)
        }.onSuccess { prepared ->
            preparedRestoreBackup = prepared
            restorePreview = prepared.preview
        }.onFailure { e ->
            activeBackupError = BackupError(
                message = e.message ?: "Failed to prepare backup import",
                actionType = BackupActionType.PREPARE_RESTORE
            )
        }
        isBackupLoading.value = false
    }

    fun dismissRestorePreview() {
        preparedRestoreBackup = null
        restorePreview = null
    }

    fun confirmRestore() {
        val prepared = preparedRestoreBackup ?: return
        viewModelScope.launch {
            isBackupLoading.value = true
            activeBackupError = null
            runCatching {
                restoreBackup(prepared)
            }.onSuccess {
                _uiEvents.send(HomeUiEvent.ShowMessage("Backup restored"))
                dismissRestorePreview()
            }.onFailure { e ->
                activeBackupError = BackupError(
                    message = e.message ?: "Failed to restore backup",
                    actionType = BackupActionType.CONFIRM_RESTORE
                )
            }
            isBackupLoading.value = false
        }
    }

    fun dismissBackupError() {
        activeBackupError = null
    }

    fun retryBackupAction(actionType: BackupActionType) {
        dismissBackupError()
        when (actionType) {
            BackupActionType.PREPARE_RESTORE -> {
                val json = lastAttemptedJson ?: return
                viewModelScope.launch {
                    prepareRestore(json)
                }
            }
            BackupActionType.CONFIRM_RESTORE -> {
                confirmRestore()
            }
            BackupActionType.LOAD_SAMPLE -> {
                loadSampleData()
            }
        }
    }

    internal fun updateLocationForTesting(location: GeoPoint?) {
        currentLocation.value = location
    }
}

internal fun filterHomePlaces(
    places: List<Place>,
    query: String,
    tags: Set<String>,
    likedFilter: Boolean? = null,
    categories: Set<PlaceCategory> = emptySet(),
    visitFilter: HomeVisitFilter = HomeVisitFilter.ALL,
    visitSummaries: Map<String, PlaceVisitSummary> = emptyMap(),
): List<Place> {
    // Normalized once here, not per place — normalization is regex work.
    val normalizedQuery = normalizeForComparison(query)
    return places.filter {
        it.matchesHomeFilters(normalizedQuery, tags, likedFilter, categories) &&
            it.matchesVisitFilter(visitFilter, visitSummaries[it.id])
    }
}

/** 가름 기준은 [isWantToGo] 하나다 — 지도의 속 빈 핀(R-15)과 같은 함수를 읽는다. */
internal fun Place.matchesVisitFilter(filter: HomeVisitFilter, summary: PlaceVisitSummary?): Boolean {
    if (filter == HomeVisitFilter.ALL) return true
    val wantToGo = isWantToGo(summary)
    return if (filter == HomeVisitFilter.WANT_TO_GO) wantToGo else !wantToGo
}

/** The [PlaceCategory] values [places] actually infer to, in declaration order. */
internal fun availableCategories(places: List<Place>): List<PlaceCategory> {
    val present = places.mapNotNullTo(mutableSetOf()) { PlaceCategory.fromTags(it.tags) }
    return PlaceCategory.entries.filter { it in present }
}

internal fun orderHomePlaces(
    places: List<Place>,
    sortMode: HomeSortMode,
    visitSummaries: Map<String, PlaceVisitSummary>,
    location: GeoPoint?,
): List<Place> = when (sortMode) {
    HomeSortMode.DEFAULT -> places
    HomeSortMode.MOST_RECENT -> places.sortedWith(
        compareByDescending<Place> { visitSummaries[it.id]?.lastVisitedAt ?: Long.MIN_VALUE }
            .thenByDescending { it.createdAt },
    )
    HomeSortMode.NEAREST -> location?.let { target ->
        places.sortedWith(
            compareBy<Place> { place -> distanceMetersOrMax(place, target) }
                .thenByDescending { it.createdAt },
        )
    } ?: places
    // 같은 정렬 키를 가진 이름(대소문자만 다른 것 등)은 원래 이름으로 한 번 더 가른다 —
    // 안 그러면 목록 순서가 저장소가 준 순서에 따라 흔들린다.
    HomeSortMode.NAME -> places.sortedWith(
        compareBy<Place> { placeNameSortKey(it.name) }.thenBy { it.name },
    )
}

/**
 * 글자 → 그 글자로 시작하는 첫 항목의 인덱스. 이름순으로 정렬된 목록에서만 뜻이 있으므로
 * 다른 정렬에서는 빈 맵이다.
 *
 * 알파벳 26자를 전부 세우지 않고 **실제로 있는 글자만** 담는다. 장소가 열한 개인 목록 옆에
 * 26줄짜리 레일이 서면 레일이 목록보다 길다(2026-09-08 피드백 §9.2).
 */
/**
 * 이 개수 미만이면 레일을 그리지 않는다. 열한 줄짜리 목록 옆의 색인은 장식이고, 색인이
 * 실제로 값을 하는 지점은 목록이 한 화면을 넘길 때부터다(§9.2).
 */
const val MIN_PLACES_FOR_ALPHABET_RAIL = 8

internal fun alphabetIndex(places: List<Place>, sortMode: HomeSortMode): Map<Char, Int> {
    if (sortMode != HomeSortMode.NAME) return emptyMap()
    val index = LinkedHashMap<Char, Int>()
    places.forEachIndexed { position, place ->
        index.getOrPut(alphabetBucket(place.name)) { position }
    }
    return index
}

internal fun Place.matchesHomeFilters(
    normalizedQuery: String,
    tags: Set<String>,
    likedFilter: Boolean? = null,
    categories: Set<PlaceCategory> = emptySet(),
): Boolean {
    // 이름만 보던 검색을 태그·동네까지 넓혔다 — "koreatown"처럼 이름에 없고 태그나
    // locationContext에만 있는 단어로 찾는 게 실제 사용 패턴이었다. note는 일부러 뺐다:
    // 자유 서술이라 걸리는 순간 결과가 거의 안 좁혀진다.
    val matchesQuery = normalizedQuery.isBlank() ||
        normalizeForComparison(name).contains(normalizedQuery) ||
        this.tags.any { normalizeForComparison(it).contains(normalizedQuery) } ||
        locationContext?.let { normalizeForComparison(it).contains(normalizedQuery) } == true
    val matchesTags = tags.isEmpty() || run {
        val normalizedTags = this.tags.map(::normalizeForComparison).toSet()
        tags.all { it in normalizedTags }
    }
    val matchesLiked = likedFilter == null || liked == likedFilter
    val matchesCategory = categories.isEmpty() || PlaceCategory.fromTags(this.tags) in categories
    return matchesQuery && matchesTags && matchesLiked && matchesCategory
}

/** Destructurable carrier for the filter flows — [combine] arity, not a domain concept. */
private data class FilterSelections(
    val tags: Set<String>,
    val selectedPlaceId: String?,
    val likedFilter: Boolean?,
    val categories: Set<PlaceCategory>,
    val visitFilter: HomeVisitFilter,
)

private fun distanceMetersOrMax(place: Place, location: GeoPoint): Float {
    val latitude = place.latitude ?: return Float.MAX_VALUE
    val longitude = place.longitude ?: return Float.MAX_VALUE
    return haversineMeters(location, GeoPoint(latitude, longitude))
}

/**
 * A place whose [fyi.moko.hotpl.domain.model.RevisitWindow] is open now — "it's a good time to
 * go back". Distinct from [RecallCandidate] (milestone recall: "7 weeks ago today"), which
 * surfaces the *past*; this surfaces the *present opportunity*.
 *
 * Only places with an explicit [fyi.moko.hotpl.domain.model.RevisitCadence] are considered —
 * places without a cadence have no window to check. Soft-deleted and non-VISITED places are
 * excluded (matching the `todaysRecalls` guard).
 */
data class RevisitCandidate(
    val placeId: String,
    val placeName: String,
    val cadenceLabel: String,
    val daysSinceLastVisit: Int?,
)

/**
 * Computes the "good to revisit now" list for the home card. Pure — no platform references.
 * Places without a [fyi.moko.hotpl.domain.model.RevisitCadence] are skipped; the calculator's
 * over-visit guard (14-day exclusion) applies automatically.
 */
internal fun revisitCandidates(
    places: List<Place>,
    visitCountByPlace: Map<String, Int>,
    visitSummaries: Map<String, PlaceVisitSummary>,
    nowMs: Long,
    calculator: RevisitWindowCalculator = RevisitWindowCalculator(),
): List<RevisitCandidate> {
    return places
        .filter { !it.isDeleted && it.status == fyi.moko.hotpl.domain.model.PlaceStatus.VISITED }
        .filter { it.revisitCadence != null }
        .mapNotNull { place ->
            val cadence = place.revisitCadence ?: return@mapNotNull null
            val summary = visitSummaries[place.id]
            val window = calculator.calculate(
                cadence = cadence,
                lastVisitedAt = summary?.lastVisitedAt,
                visitCount = visitCountByPlace[place.id] ?: summary?.visitCount ?: 0,
                nowMs = nowMs,
            )
            if (window.isGoodNow) {
                val daysSince = summary?.lastVisitedAt?.let { lastVisit ->
                    val zone = kotlinx.datetime.TimeZone.currentSystemDefault()
                    val today = kotlin.time.Instant.fromEpochMilliseconds(nowMs)
                        .toLocalDateTime(zone).date
                    val past = kotlin.time.Instant.fromEpochMilliseconds(lastVisit)
                        .toLocalDateTime(zone).date
                    past.daysUntil(today)
                }
                RevisitCandidate(
                    placeId = place.id,
                    placeName = place.name,
                    cadenceLabel = cadenceLabel(cadence),
                    daysSinceLastVisit = daysSince,
                )
            } else {
                null
            }
        }
        .sortedWith(compareByDescending<RevisitCandidate> { it.daysSinceLastVisit ?: 0 }.thenBy { it.placeName })
}

internal fun cadenceLabel(cadence: fyi.moko.hotpl.domain.model.RevisitCadence): String = when (cadence) {
    fyi.moko.hotpl.domain.model.RevisitCadence.ANYTIME -> "Anytime"
    fyi.moko.hotpl.domain.model.RevisitCadence.OCCASIONAL -> "Occasional"
    fyi.moko.hotpl.domain.model.RevisitCadence.SPECIAL -> "Special"
    fyi.moko.hotpl.domain.model.RevisitCadence.ANNUAL -> "Annual"
}
